import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock3,
  GraduationCap,
  Plus,
  ShieldCheck,
  UsersRound,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSession } from '@/hooks/useSession';
import {
  getMyTrainingAssignments,
  getTrainingCompliance,
} from '../api/trainingApi';
import { TrainingAssignmentsTable } from '../components/training/TrainingAssignmentsTable';
import { TrainingComplianceBadge } from '../components/training/TrainingStatusBadge';
import { TrainingProfileDialog } from '../components/training/TrainingProfileDialog';
import {
  useMyTrainingAssignments,
  useMyTrainingSummary,
  useSaveTrainingProfile,
  useTrainingCompliance,
  useTrainingDirectory,
  useTrainingProfiles,
  useTrainingSupervisorAlerts,
} from '../hooks/useTraining';
import type {
  TrainingAssignment,
  TrainingFilters,
  TrainingProfile,
} from '../types/training.types';
import { MyUpdateRequests } from '../components/eqms/update-requests/MyUpdateRequests';

const PAGE_LENGTH = 25;
const QUALITY_ROLES = ['full_admin', 'director', 'quality_manager'];
const STATUS_OPTIONS: Array<{ value: NonNullable<TrainingFilters['status']>; label: string }> = [
  { value: 'all', label: 'All assignments' },
  { value: 'open', label: 'Open' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'locked', label: 'Locked' },
];

function useTrainingTableState() {
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<TrainingFilters['status']>('all');
  const [sortKey, setSortKey] = useState<TrainingFilters['sortKey']>('dueAt');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');

  const columnSearch = useMemo(
    () => ['employee', 'documentControlCode', 'documentName']
      .map((key) => columnFilters[key]?.trim())
      .filter(Boolean)
      .join(' '),
    [columnFilters],
  );
  const documentGroup = columnFilters.documentGroup?.trim() || '';

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(columnSearch);
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [columnSearch]);

  const filters = useMemo<TrainingFilters>(() => ({
    status,
    search: search || undefined,
    documentGroup: documentGroup || undefined,
    pageNumber: page,
    pageLength: PAGE_LENGTH,
    sortKey,
    sortDirection,
  }), [documentGroup, page, search, sortDirection, sortKey, status]);

  const onSort = (key: TrainingFilters['sortKey']) => {
    if (key === sortKey) setSortDirection((current) => current === 1 ? -1 : 1);
    else {
      setSortKey(key);
      setSortDirection(1);
    }
    setPage(0);
  };

  return {
    page,
    setPage,
    status,
    setStatus: (value: TrainingFilters['status']) => {
      setStatus(value);
      setPage(0);
    },
    sortKey,
    sortDirection,
    columnFilters,
    setColumnFilter: (key: string, value: string) => {
      setColumnFilters((current) => ({ ...current, [key]: value }));
    },
    clearFilters: () => {
      setColumnFilters({});
      setStatus('all');
      setPage(0);
    },
    filters,
    onSort,
  };
}

async function fetchAllAssignments(
  filters: TrainingFilters,
  fetchPage: (filters: TrainingFilters) => Promise<{ assignments: TrainingAssignment[]; total: number }>,
) {
  const assignments: TrainingAssignment[] = [];
  let pageNumber = 0;
  let total: number;
  do {
    const batch = await fetchPage({ ...filters, pageNumber, pageLength: 100 });
    assignments.push(...batch.assignments);
    total = batch.total;
    pageNumber += 1;
  } while (assignments.length < total);
  return assignments;
}

export function TrainingPortalPage() {
  const navigate = useNavigate();
  const { user } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const isQuality = Boolean(user?.postgres_roles?.some((role) => QUALITY_ROLES.includes(role)));
  const requestedTab = searchParams.get('tab');
  const activeTab = requestedTab === 'compliance' && isQuality
    ? 'compliance'
    : requestedTab === 'matrix' && isQuality
      ? 'matrix'
      : 'my-training';
  const myState = useTrainingTableState();
  const complianceState = useTrainingTableState();
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<TrainingProfile | null>(null);

  const summaryQuery = useMyTrainingSummary();
  const assignmentsQuery = useMyTrainingAssignments(myState.filters);
  const complianceQuery = useTrainingCompliance(complianceState.filters, isQuality);
  const profilesQuery = useTrainingProfiles(isQuality);
  const directoryQuery = useTrainingDirectory(isQuality);
  const alertsQuery = useTrainingSupervisorAlerts();
  const profileMutation = useSaveTrainingProfile();

  const complianceAssignments = complianceQuery.data?.assignments || [];
  const complianceCounts = complianceAssignments.reduce(
    (counts, assignment) => ({ ...counts, [assignment.complianceState]: counts[assignment.complianceState] + 1 }),
    { green: 0, yellow: 0, red: 0 },
  );

  const openProfile = (profile?: TrainingProfile) => {
    setSelectedProfile(profile || null);
    setProfileDialogOpen(true);
  };

  return (
    <div className="min-h-full bg-slate-50">
      <section className="overflow-hidden bg-slate-950 text-white">
        <div className="relative mx-auto max-w-[1600px] px-4 py-8 sm:px-6 lg:px-8">
          <div className="absolute -right-16 -top-24 h-72 w-72 rounded-full bg-teal-500/10 blur-3xl" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="mb-3 flex items-center gap-2">
                <Badge className="border-teal-400/20 bg-teal-400/10 text-teal-200 hover:bg-teal-400/10">
                  <GraduationCap className="mr-1 h-3.5 w-3.5" /> eQMS · Training Management
                </Badge>
                <span className="font-mono text-xs text-slate-400">PHASE 3</span>
              </div>
              <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                Qualification follows the evidence.
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
                Effective procedures become assigned training, reading becomes attributable evidence,
                and qualification is recorded only after the required assessment and signature.
              </p>
            </div>
            {isQuality && (
              <Button
                className="w-fit bg-teal-500 text-slate-950 hover:bg-teal-400"
                onClick={() => openProfile()}
              >
                <Plus className="mr-2 h-4 w-4" /> New training matrix
              </Button>
            )}
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-[1600px] space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {alertsQuery.data?.alerts.map((alert) => (
          <Link
            key={alert.id}
            to={`/eqms/training/${alert.assignmentId}`}
            className="flex items-start justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-950"
          >
            <div className="flex gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-700" />
              <div>
                <div className="text-sm font-semibold">Supervisor action required</div>
                <div className="mt-1 text-sm text-rose-800">
                  {alert.employeeName} · {alert.documentControlCode} · {alert.reason}
                </div>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0" />
          </Link>
        ))}

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {[
            { label: 'Assigned', value: summaryQuery.data?.assigned, icon: GraduationCap, tone: 'bg-slate-100 text-slate-700' },
            { label: 'Due', value: summaryQuery.data?.due, icon: Clock3, tone: 'bg-amber-50 text-amber-700' },
            { label: 'Overdue', value: summaryQuery.data?.overdue, icon: AlertTriangle, tone: 'bg-rose-50 text-rose-700' },
            { label: 'Completed', value: summaryQuery.data?.completed, icon: CheckCircle2, tone: 'bg-teal-50 text-teal-700' },
            { label: 'Evidence', value: 'SIGNED', icon: ShieldCheck, tone: 'bg-blue-50 text-blue-700' },
          ].map((metric) => (
            <Card key={metric.label} className="border-slate-200 shadow-sm">
              <CardContent className="flex items-center justify-between p-5">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{metric.label}</div>
                  {summaryQuery.isLoading && typeof metric.value !== 'string'
                    ? <Skeleton className="mt-2 h-8 w-12" />
                    : <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-950">{metric.value ?? 0}</div>}
                </div>
                <div className={`rounded-xl p-2.5 ${metric.tone}`}><metric.icon className="h-5 w-5" /></div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(value) => setSearchParams(value === 'my-training' ? {} : { tab: value })}
          className="space-y-4"
        >
          <TabsList className="h-auto flex-wrap justify-start bg-white p-1 shadow-sm">
            <TabsTrigger value="my-training">My training</TabsTrigger>
            {isQuality && <TabsTrigger value="compliance">Compliance register</TabsTrigger>}
            {isQuality && <TabsTrigger value="matrix">Training matrix</TabsTrigger>}
          </TabsList>

          <TabsContent value="my-training" className="space-y-4">
            <div className="flex justify-end">
              <Select
                value={myState.status || 'all'}
                onValueChange={(value) => myState.setStatus(value as TrainingFilters['status'])}
              >
                <SelectTrigger className="w-full bg-white sm:w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <TrainingAssignmentsTable
              assignments={assignmentsQuery.data?.assignments || []}
              total={assignmentsQuery.data?.total || 0}
              page={myState.page}
              pageLength={PAGE_LENGTH}
              sortKey={myState.sortKey}
              sortDirection={myState.sortDirection}
              columnFilters={myState.columnFilters}
              status={myState.status}
              isLoading={assignmentsQuery.isLoading}
              isRefreshing={assignmentsQuery.isFetching}
              onColumnFilterChange={myState.setColumnFilter}
              onClearFilters={myState.clearFilters}
              onSort={myState.onSort}
              onPageChange={myState.setPage}
              onRowClick={(assignment) => navigate(`/eqms/training/${assignment.id}`)}
              onRefresh={() => assignmentsQuery.refetch()}
              onExportAll={() => fetchAllAssignments(myState.filters, getMyTrainingAssignments)}
            />
            <MyUpdateRequests />
          </TabsContent>

          {isQuality && (
            <TabsContent value="compliance" className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                {(['green', 'yellow', 'red'] as const).map((state) => (
                  <Card key={state} className="border-slate-200 shadow-sm">
                    <CardContent className="flex items-center justify-between p-4">
                      <TrainingComplianceBadge state={state} />
                      <div className="text-2xl font-semibold tabular-nums">{complianceCounts[state]}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="flex justify-end">
                <Select
                  value={complianceState.status || 'all'}
                  onValueChange={(value) => complianceState.setStatus(value as TrainingFilters['status'])}
                >
                  <SelectTrigger className="w-full bg-white sm:w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <TrainingAssignmentsTable
                complianceMode
                assignments={complianceAssignments}
                total={complianceQuery.data?.total || 0}
                page={complianceState.page}
                pageLength={PAGE_LENGTH}
                sortKey={complianceState.sortKey}
                sortDirection={complianceState.sortDirection}
                columnFilters={complianceState.columnFilters}
                status={complianceState.status}
                isLoading={complianceQuery.isLoading}
                isRefreshing={complianceQuery.isFetching}
                onColumnFilterChange={complianceState.setColumnFilter}
                onClearFilters={complianceState.clearFilters}
                onSort={complianceState.onSort}
                onPageChange={complianceState.setPage}
                onRowClick={(assignment) => navigate(`/eqms/training/${assignment.id}`)}
                onRefresh={() => complianceQuery.refetch()}
                onExportAll={() => fetchAllAssignments(complianceState.filters, getTrainingCompliance)}
              />
            </TabsContent>
          )}

          {isQuality && (
            <TabsContent value="matrix">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="flex-row items-center justify-between border-b border-slate-100">
                  <div>
                    <CardTitle className="text-base">Assignment rules</CardTitle>
                    <p className="mt-1 text-sm text-slate-500">
                      Profiles are matched to controlled-document departments at Effective release.
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => openProfile()}>
                    <Plus className="mr-1 h-4 w-4" /> Add matrix
                  </Button>
                </CardHeader>
                <CardContent className="p-0">
                  {profilesQuery.isLoading && (
                    <div className="space-y-3 p-5">
                      {Array.from({ length: 3 }).map((_, index) => (
                        <Skeleton key={index} className="h-20 w-full" />
                      ))}
                    </div>
                  )}
                  {!profilesQuery.isLoading && (profilesQuery.data?.profiles.length || 0) === 0 && (
                    <div className="py-16 text-center">
                      <UsersRound className="mx-auto h-9 w-9 text-slate-300" />
                      <div className="mt-3 text-sm font-medium text-slate-800">No training matrices yet</div>
                      <div className="mt-1 text-xs text-slate-500">Create one before the next controlled release.</div>
                    </div>
                  )}
                  {profilesQuery.data?.profiles.map((profile) => (
                    <button
                      key={profile.id}
                      type="button"
                      onClick={() => openProfile(profile)}
                      className="flex w-full items-center justify-between gap-4 border-b border-slate-100 px-5 py-4 text-left last:border-0 hover:bg-slate-50"
                    >
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-slate-950">{profile.name}</span>
                          <Badge variant="outline">{profile.documentGroup}</Badge>
                          {!profile.active && <Badge variant="secondary">Inactive</Badge>}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {profile.members.length} employees · {profile.gracePeriodDays} days ·{' '}
                          {profile.quizRequired
                            ? `${profile.questions.length} questions, ${profile.passingScore}% pass, ${profile.maxAttempts} attempts`
                            : 'reading attestation only'}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                    </button>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>

      <TrainingProfileDialog
        open={profileDialogOpen}
        profile={selectedProfile}
        directory={directoryQuery.data?.employees || []}
        isPending={profileMutation.isPending}
        onOpenChange={(open) => {
          setProfileDialogOpen(open);
          if (!open) setSelectedProfile(null);
        }}
        onSave={(input) => profileMutation.mutateAsync({
          id: selectedProfile?.id,
          input,
        }).then(() => {
          setProfileDialogOpen(false);
          setSelectedProfile(null);
        })}
      />
    </div>
  );
}
